import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { parsePgn, type ParsedGame } from "@onchainreps/chess";
import { StockfishEngine } from "@onchainreps/engine";
import { AccountPanel } from "./components/AccountPanel";
import { AppBar, type AppTab } from "./components/AppBar";
import { BottomNav } from "./components/BottomNav";
import { BoardStage } from "./components/BoardStage";
import { CheckIcon, CopyIcon, KeyboardIcon } from "./components/Icons";
import { KeyboardHelp } from "./components/KeyboardHelp";
import { BotGame } from "./components/BotGame";
import { EnginePanel } from "./components/EnginePanel";
import { EvalCurve } from "./components/EvalCurve";
import { GamesDashboard } from "./components/GamesDashboard";
import { ImportSheet } from "./components/ImportSheet";
import { MoveTable } from "./components/MoveTable";
import { OpeningExplorer } from "./components/OpeningExplorer";
import { OpeningPanel } from "./components/OpeningPanel";
import { OpeningStrip } from "./components/OpeningStrip";
import { Planned } from "./components/Planned";
import { ReportPanel } from "./components/ReportPanel";
import { TrainerHome } from "./components/TrainerHome";
import {
  computeReport,
  computeReviews,
  positionFens,
  toPositionAnalysis,
  type MoveReview,
  type PositionAnalysis,
} from "./lib/analysis";
import { EXAMPLE_PGN } from "./lib/exampleGame";
import { loadCourseCatalog, matchOpening, type CourseCatalog } from "./lib/openings";
import { persistAnalysis, type ImportMeta } from "./lib/persist";
import { useLocalStore } from "./lib/useLocalStore";
import { useTheme } from "./lib/useTheme";

const SWEEP_DEPTH = 8;
const FOCUS_DEPTH = 15;
const MAX_SWEEP_WORKERS = 2;

type EngineState = "idle" | "loading" | "ready" | "error";
type PanelTab = "moves" | "report" | "opening";

export function App() {
  const [tab, setTab] = useState<AppTab>("analyze");
  const [panelTab, setPanelTab] = useState<PanelTab>("moves");
  const [game, setGame] = useState<ParsedGame | null>(null);
  const [positions, setPositions] = useState<PositionAnalysis[]>([]);
  const [reviews, setReviews] = useState<MoveReview[]>([]);
  const [ply, setPly] = useState(0);
  const [orientation, setOrientation] = useState<"white" | "black">("white");
  const [engineState, setEngineState] = useState<EngineState>("idle");
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [sheetOpen, setSheetOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [catalog, setCatalog] = useState<CourseCatalog | null>(null);
  const [error, setError] = useState<string | null>(null);
  const store = useLocalStore();
  const theme = useTheme();

  const engineRef = useRef<StockfishEngine | null>(null);
  const sweepRef = useRef<StockfishEngine[]>([]);
  const cancelRef = useRef(false);
  const focusTokenRef = useRef(0);
  const metaRef = useRef<ImportMeta>({ source: "pgn" });

  const fens = useMemo(() => (game ? positionFens(game) : []), [game]);
  const report = useMemo(() => (game ? computeReport(game, reviews) : null), [game, reviews]);

  const runAnalysis = useCallback(async (parsed: ParsedGame) => {
    cancelRef.current = true;
    cancelRef.current = false;

    sweepRef.current.forEach((engine) => engine.dispose());
    sweepRef.current = [];

    const cores = typeof navigator !== "undefined" ? navigator.hardwareConcurrency || 2 : 2;
    const workerCount = Math.max(1, Math.min(MAX_SWEEP_WORKERS, cores - 1));
    const pool = Array.from(
      { length: workerCount },
      () => new StockfishEngine({ threads: 1, hashMb: 16, multiPv: 1 }),
    );
    sweepRef.current = pool;

    const positionsFens = positionFens(parsed);
    const collected: Array<PositionAnalysis | null> = new Array(positionsFens.length).fill(null);

    setEngineState("loading");
    setError(null);
    setPositions([]);
    setReviews([]);
    setProgress({ done: 0, total: positionsFens.length });

    const publish = () => {
      const ready: PositionAnalysis[] = [];
      for (const item of collected) {
        if (!item) break;
        ready.push(item);
      }
      setPositions([...ready]);
      setProgress({ done: ready.length, total: positionsFens.length });
      const usable = collected.filter((item): item is PositionAnalysis => item !== null);
      if (usable.length > 1) setReviews(computeReviews(parsed, usable));
    };

    try {
      await Promise.all(pool.map((engine) => engine.init()));

      let next = 0;
      const worker = async (engine: StockfishEngine) => {
        while (true) {
          if (cancelRef.current) return;
          const index = next;
          next += 1;
          if (index >= positionsFens.length) return;
          const fen = positionsFens[index]!;
          const result = await engine.analyse(fen, { depth: SWEEP_DEPTH });
          if (cancelRef.current) return;
          collected[index] = toPositionAnalysis(index, fen, result);
          publish();
        }
      };

      await Promise.all(pool.map((engine) => worker(engine)));
      if (cancelRef.current) return;

      const complete = collected.filter((item): item is PositionAnalysis => item !== null);
      const finalReviews = computeReviews(parsed, complete);
      setReviews(finalReviews);
      setEngineState("ready");

      if (store) {
        const report = computeReport(parsed, finalReviews);
        void persistAnalysis(store, parsed, complete, finalReviews, report, metaRef.current);
      }
    } catch (cause) {
      if (cancelRef.current) return;
      setEngineState("error");
      setError(cause instanceof Error ? cause.message : "The engine could not start.");
    }
  }, [store]);

  const loadPgn = useCallback(
    (raw: string, meta: ImportMeta = { source: "pgn" }) => {
      try {
        const parsed = parsePgn(raw);
        if (parsed.moves.length === 0) throw new Error("That PGN has no moves.");
        metaRef.current = meta;
        setGame(parsed);
        setPly(0);
        setOrientation("white");
        setTab("analyze");
        void runAnalysis(parsed);
      } catch (cause) {
        setError(cause instanceof Error ? cause.message : "Could not read that PGN.");
        setEngineState("error");
      }
    },
    [runAnalysis],
  );

  const selectPly = useCallback(
    (nextPly: number) => {
      setPly(nextPly);
      const known = positions[nextPly];
      if (!game) return;
      const targetDepth = known ? FOCUS_DEPTH : 14;
      if (known && known.depth >= FOCUS_DEPTH) return;

      const token = ++focusTokenRef.current;

      void (async () => {
        if (!engineRef.current) {
          engineRef.current = new StockfishEngine({ threads: 1, hashMb: 32, multiPv: 3 });
          await engineRef.current.init();
        }
        const engine = engineRef.current;
        if (!engine) return;
        const fen = known?.fen ?? fens[nextPly];
        if (!fen) return;
        const result = await engine.analyse(fen, { depth: targetDepth });
        if (token !== focusTokenRef.current) return;
        const refined = toPositionAnalysis(nextPly, fen, result);
        setPositions((current) => {
          const next = [...current];
          while (next.length < nextPly) {
            next.push({
              ply: next.length,
              fen: fens[next.length] ?? "",
              sideToMove: "white",
              score: null,
              winPercentWhite: 50,
              bestMove: null,
              alternatives: 0,
              depth: 0,
              pv: [],
              lines: [],
            });
          }
          next[nextPly] = refined;
          return next;
        });
        setReviews(
          computeReviews(
            game,
            positions.map((item) => (item.ply === nextPly ? refined : item)),
          ),
        );
      })().catch(() => undefined);
    },
    [fens, game, positions],
  );

  useEffect(() => {
    let active = true;
    void loadCourseCatalog()
      .then((loaded) => {
        if (active) setCatalog(loaded);
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, []);

  const copyPgn = useCallback(() => {
    if (!game) return;
    void navigator.clipboard
      ?.writeText(game.pgn)
      .then(() => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1800);
      })
      .catch(() => undefined);
  }, [game]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable)
      ) {
        return;
      }
      if (tab !== "analyze" || !game) return;

      switch (event.key) {
        case "ArrowLeft":
          selectPly(Math.max(0, ply - 1));
          break;
        case "ArrowRight":
          selectPly(Math.min(fens.length - 1, ply + 1));
          break;
        case "ArrowUp":
          selectPly(Math.min(fens.length - 1, ply + 2));
          break;
        case "ArrowDown":
          selectPly(Math.max(0, ply - 2));
          break;
        case "Home":
          selectPly(0);
          break;
        case "End":
          selectPly(fens.length - 1);
          break;
        case "f":
        case "F":
          setOrientation((value) => (value === "white" ? "black" : "white"));
          break;
        case "c":
        case "C":
          copyPgn();
          break;
        default:
          return;
      }
      event.preventDefault();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [copyPgn, fens.length, game, ply, selectPly, tab]);

  useEffect(() => {
    return () => {
      cancelRef.current = true;
      sweepRef.current.forEach((engine) => engine.dispose());
      engineRef.current?.dispose();
    };
  }, []);

  const currentPosition = positions[ply];
  const currentFen = currentPosition?.fen ?? fens[ply] ?? "";
  const lastMove = game?.moves[ply - 1];

  const openingMatch = useMemo(() => {
    if (!catalog || !game) return null;
    return matchOpening(catalog, game.moves.slice(0, ply).map((move) => move.san));
  }, [catalog, game, ply]);

  return (
    <div className="app">
      <AppBar
        tab={tab}
        onTab={setTab}
        engineState={engineState}
        depth={currentPosition?.depth ?? null}
        progress={progress}
        onImport={() => setSheetOpen(true)}
        theme={theme.resolved}
        onToggleTheme={theme.toggle}
      />

      <main className="workspace">
        {tab === "analyze" ? (
          <>
            <div className="board-column">
              {game ? (
                <>
                  {openingMatch ? (
                    <OpeningStrip
                      name={openingMatch.line.name}
                      eco={openingMatch.line.eco}
                      matched={openingMatch.matched}
                    />
                  ) : null}
                  <BoardStage
                    fen={currentFen}
                    orientation={orientation}
                    onFlip={() => setOrientation((value) => (value === "white" ? "black" : "white"))}
                    onFirst={() => selectPly(0)}
                    onPrev={() => selectPly(Math.max(0, ply - 1))}
                    onNext={() => selectPly(Math.min(fens.length - 1, ply + 1))}
                    onLast={() => selectPly(fens.length - 1)}
                    canGoBack={ply > 0}
                    canGoForward={ply < fens.length - 1}
                    whiteWinPercent={currentPosition?.winPercentWhite ?? null}
                    score={currentPosition?.score ?? null}
                    sideToMove={currentPosition?.sideToMove ?? "white"}
                    bestMove={currentPosition?.bestMove ?? null}
                    lastMove={lastMove ? { from: lastMove.uci.slice(0, 2), to: lastMove.uci.slice(2, 4) } : null}
                  />
                  <EnginePanel position={currentPosition} />
                  <EvalCurve
                    positions={positions}
                    reviews={reviews}
                    currentPly={ply}
                    total={fens.length}
                  />
                </>
              ) : (
                <Hero onImport={() => setSheetOpen(true)} onExample={() => loadPgn(EXAMPLE_PGN)} />
              )}
            </div>

            <div className="panel-column">
              {game ? (
                <section className="panel">
                  <div className="panel__head">
                    <h1 className="panel__title">
                      {game.headers.White ?? "White"} vs {game.headers.Black ?? "Black"}
                    </h1>
                    {game.opening || game.headers.Opening ? (
                      <span className="panel__subtitle">{game.opening ?? game.headers.Opening}</span>
                    ) : null}
                    <div className="panel__actions">
                      <div className="segmented" role="tablist">
                        {(
                          [
                            ["moves", "Moves"],
                            ["report", "Report"],
                            ["opening", "Opening"],
                          ] as Array<[PanelTab, string]>
                        ).map(([id, label]) => (
                          <button
                            key={id}
                            role="tab"
                            aria-selected={panelTab === id}
                            onClick={() => setPanelTab(id)}
                          >
                            {label}
                          </button>
                        ))}
                      </div>
                      <div className="panel__tools">
                        <button
                          className="iconbtn"
                          onClick={copyPgn}
                          aria-label="Copy the PGN"
                          title="Copy the PGN"
                        >
                          {copied ? <CheckIcon /> : <CopyIcon />}
                        </button>
                        <button
                          className="iconbtn"
                          onClick={() => setShortcutsOpen(true)}
                          aria-label="Keyboard shortcuts"
                          title="Keyboard shortcuts"
                        >
                          <KeyboardIcon />
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="panel__body panel__body--flush">
                    {panelTab === "moves" ? (
                      <MoveTable game={game} reviews={reviews} currentPly={ply} onSelect={selectPly} />
                    ) : null}
                    {panelTab === "report" && report ? (
                      <div className="panel__body">
                        <ReportPanel
                          game={game}
                          report={report}
                          reviews={reviews}
                          onSelect={(ply) => {
                            selectPly(ply);
                            setPanelTab("moves");
                          }}
                        />
                      </div>
                    ) : null}
                    {panelTab === "opening" ? (
                      <div className="panel__body">
                        <OpeningPanel game={game} />
                      </div>
                    ) : null}
                  </div>
                </section>
              ) : (
                <Planned
                  kicker="What this does"
                  title="Every game you have played, read move by move"
                  body="OnchainReps runs a Stockfish engine inside your browser, so analysis is free, instant and private. Nothing is uploaded, no account is required, and there is no premium tier hiding the useful parts."
                  items={[
                    {
                      name: "Board and engine",
                      desc: "Live evaluation, principal variation and the best move on the board.",
                      done: true,
                    },
                    {
                      name: "Game report",
                      desc: "Move-by-move classification, accuracy for both sides and your costliest mistake.",
                      done: true,
                    },
                    {
                      name: "Opening trainer",
                      desc: "Courses with spaced repetition, built from open data and our own writing.",
                    },
                    {
                      name: "Tactics trainer",
                      desc: "Rated puzzle sessions and drills built from the mistakes you actually make.",
                    },
                  ]}
                />
              )}
            </div>
          </>
        ) : null}

        {tab === "train" ? (
          <div className="panel-column" style={{ gridColumn: "1 / -1" }}>
            <TrainerHome store={store} />
          </div>
        ) : null}

        {tab === "explore" ? (
          <div className="panel-column" style={{ gridColumn: "1 / -1" }}>
            <OpeningExplorer onOpenLine={(pgn) => loadPgn(pgn, { source: "pgn" })} />
          </div>
        ) : null}

        {tab === "games" ? (
          <div className="panel-column" style={{ gridColumn: "1 / -1" }}>
            <GamesDashboard store={store} onOpen={(pgn) => loadPgn(pgn, { source: "pgn" })} />
          </div>
        ) : null}

        {tab === "me" ? (
          <div className="panel-column" style={{ gridColumn: "1 / -1" }}>
            <AccountPanel store={store} />
          </div>
        ) : null}
      </main>

      <BottomNav tab={tab} onTab={setTab} />

      <ImportSheet open={sheetOpen} onClose={() => setSheetOpen(false)} onLoadPgn={loadPgn} />

      {shortcutsOpen ? <KeyboardHelp onClose={() => setShortcutsOpen(false)} /> : null}

      {error ? (
        <div
          role="status"
          style={{
            position: "fixed",
            left: 16,
            right: 16,
            bottom: "calc(var(--nav-height) + var(--safe-bottom) + 16px)",
            zIndex: 50,
            padding: "12px 16px",
            borderRadius: "var(--radius-sm)",
            background: "var(--crimson-wash)",
            color: "var(--crimson)",
            fontSize: "var(--text-sm)",
          }}
        >
          {error}
        </div>
      ) : null}
    </div>
  );
}

interface HeroProps {
  onImport: () => void;
  onExample: () => void;
}

function Hero({ onImport, onExample }: HeroProps) {
  return (
    <section className="hero">
      <span className="hero__kicker">Free, no account, no paywall</span>
      <h1 className="hero__title">
        Every game you have played,
        <br />
        read move by move.
      </h1>
      <p className="hero__body">
        Stockfish runs inside this page, so the analysis is fast, private and free to use as much as you
        like. Bring a PGN, or pull your latest games from Lichess or Chess.com.
      </p>
      <div className="hero__actions">
        <button className="btn btn--primary" onClick={onImport}>
          Bring a game
        </button>
        <button className="btn btn--ghost" onClick={onExample}>
          Try the Immortal Game
        </button>
      </div>
      <ul className="hero__points">
        <li>Evaluation, principal variation and best-move arrows</li>
        <li>Move classification and accuracy for both sides</li>
        <li>Opening names from open data, no account needed</li>
      </ul>
    </section>
  );
}
